import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { cn } from '@/lib/utils';
import type { LoyaltyRedeemLine } from '@kgc/api-contract';
import { useTranslation } from '@kgc/i18n';
import { useEffect, useState } from 'react';
import { reserveLoyaltyRedeem } from '../../../api/loyalty-redeem';
import { useWizardStore } from '../wizard-store';

/**
 * FE-E — hűségpont-beváltás csúszka (Wave-2 / E-105 Phase 3, ADR-103 Am-5 D-5.2).
 *
 * A csúszka CSAK reserve/preview-t küld (nem spend) — a tényleges levonás a
 * szerződés-véglegesítéskor (commit-spend, a rental-create body `loyaltyRedeem`
 * mezőjén, a draftId idempotency-kulccsal). Pont = Int-lépés. A szerver a valós
 * igazság: 50%-cap a rentalGrossAmount-ra + egyenleg-cap → lefelé clamp; a
 * preview a szerver-választ (pointsApplied/amount/capReached) mutatja, NEM a
 * kért értéket. 0 egyenleg → a csúszka nem renderel.
 */
export function LoyaltyRedeemSlider({
  partnerId,
  loyaltyPoints,
  rentalGrossAmount,
}: {
  partnerId: string;
  loyaltyPoints: number;
  rentalGrossAmount: string;
}) {
  const { t } = useTranslation();
  const draftId = useWizardStore(s => s.draftId);
  const setLoyaltyRedeem = useWizardStore(s => s.setLoyaltyRedeem);
  const [points, setPoints] = useState(0);
  const [preview, setPreview] = useState<LoyaltyRedeemLine | null>(null);
  const debouncedPoints = useDebouncedValue(points, 300);

  useEffect(() => {
    if (debouncedPoints <= 0) {
      setPreview(null);
      setLoyaltyRedeem(null);
      return;
    }
    let active = true;
    void reserveLoyaltyRedeem({
      partnerId,
      draftId,
      pointsToRedeem: debouncedPoints,
      rentalGrossAmount,
    }).then(line => {
      if (!active) return;
      setPreview(line);
      setLoyaltyRedeem(line);
    });
    return () => {
      active = false;
    };
  }, [debouncedPoints, partnerId, draftId, rentalGrossAmount, setLoyaltyRedeem]);

  if (loyaltyPoints <= 0) return null;

  return (
    <div className="space-y-2" data-testid="loyalty-redeem-wrapper">
      <div className="flex items-center justify-between">
        <label htmlFor="loyalty-redeem-slider" className="text-sm font-medium">
          {t('rental.loyalty.title')}
        </label>
        <span className="text-xs text-muted-foreground">
          {t('rental.loyalty.balance', { points: loyaltyPoints })}
        </span>
      </div>

      <input
        id="loyalty-redeem-slider"
        type="range"
        min={0}
        max={loyaltyPoints}
        step={1}
        value={points}
        onChange={e => setPoints(Number.parseInt(e.target.value, 10))}
        className="w-full"
        data-testid="loyalty-redeem-slider"
      />

      {preview && preview.pointsApplied > 0 && (
        <div
          className="flex items-center justify-between text-sm"
          data-testid="loyalty-redeem-preview"
        >
          <span className="text-muted-foreground">
            {t('rental.loyalty.applied', { points: preview.pointsApplied })}
          </span>
          <span className="font-semibold text-emerald-600 dark:text-emerald-400">
            −{preview.amount} {t('common.currency')}
          </span>
        </div>
      )}

      {preview?.capReached && (
        <p
          className={cn('text-xs text-amber-600 dark:text-amber-400')}
          data-testid="loyalty-redeem-cap-notice"
        >
          {t('rental.loyalty.capReached')}
        </p>
      )}
    </div>
  );
}
